import type { ProductImage } from '@cobrastyle/shared-types';

/** Just the part of a variant this needs, so tests need not build a whole one. */
interface HasImages {
  images?: ProductImage[];
}

/**
 * Which images the gallery draws: the selected variant's own, followed by the
 * master's.
 *
 * Torsbo's feedback (p.14) asks for the first half — "Val: Desert Orange / Då
 * visas bilder för den specifikationen" — and describes the fallback in the
 * same breath: "Alla artiklar är inte upplagda med specifika bilder per
 * attribut och så visas bara bilderna från Masterartikeln."
 *
 * ## Why the two sets are combined rather than swapped
 *
 * Because that is what the old site did, deliberately. Its theme set
 *
 *     <var name="gallery_switch_strategy">prepend</var>
 *
 * in `app/design/frontend/Torsbo/default/etc/view.xml`, and Magento's swatch
 * renderer implements `prepend` as exactly this concatenation:
 *
 *     if (gallerySwitchStrategy === 'prepend' && !isInitial) {
 *         imagesToUpdate = imagesToUpdate.concat(initialImages);
 *     }
 *
 * So the chosen colour leads the gallery and the master's generic shots stay
 * reachable behind it. Replacing outright — which this function did first —
 * loses the group photo and any detail shot that only exists on the master.
 *
 * Magento's `!isInitial` guard is the fallback below: when the variant has no
 * images of its own there is nothing to lead with, so the master's set is
 * returned alone rather than doubled.
 *
 * ## Two details Magento gets differently
 *
 * Magento tests `images[0].img === initial` to decide whether it is looking at
 * the master's set. Here the variant's own array is the signal, which is
 * exact — Magento's check misfires when a variant's first image happens to be
 * the master's.
 *
 * And this de-duplicates by URL where Magento's plain `concat` does not. A file
 * assigned to both a variant and the master would otherwise appear twice in the
 * strip, which reads as a glitch rather than as two pictures.
 */
export function galleryImages(
  variant: HasImages | null | undefined,
  productImages: ProductImage[],
): ProductImage[] {
  const own = variant?.images;
  if (!own || own.length === 0) return productImages;

  const seen = new Set(own.map((image) => image.url));
  return [...own, ...productImages.filter((image) => !seen.has(image.url))];
}
